'use client';

import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import {
  Users,
  MessageSquare,
  ArrowRight,
  Plus,
  Search,
  Star,
  Globe,
  Lock,
  TrendingUp,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCommunities } from '../../hooks/use-communities';
import { useAuthStore, selectIsAuthenticated } from '../../hooks';
import {
  getSelectedCommunity,
  setSelectedCommunity,
} from '../../utils/community-cookie';

const formatNumber = (num) => {
  if (typeof num === 'string') {
    if (num.includes(',')) return num;
    num = parseInt(num.replace(/,/g, ''), 10);
  }
  if (typeof num !== 'number' || isNaN(num)) return '0';
  if (num >= 1000) {
    return (num / 1000).toFixed(num >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'K';
  }
  return num.toLocaleString();
};

// Community card for joined communities
const JoinedCommunityCard = ({ community, index }) => {
  const navigate = useNavigate();

  const handleEnterForum = () => {
    // Set cookie to remember this community
    setSelectedCommunity(community.slug || community.oid, community.oid);
    // Navigate to forum
    navigate(`/forum/${community.slug || community.oid}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-violet-500/20 hover:border-violet-500/40 transition-all duration-300 cursor-pointer"
      onClick={handleEnterForum}
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 to-cyan-500/0 group-hover:from-violet-500/5 group-hover:to-cyan-500/5 transition-all duration-300" />

      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-xl font-bold shadow-lg shadow-violet-500/20">
              {community.name?.charAt(0) || 'C'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-lg text-white group-hover:text-violet-300 transition-colors">
                  {community.name}
                </h3>
                {community.isPrivate ? (
                  <Lock className="w-4 h-4 text-zinc-500" />
                ) : (
                  <Globe className="w-4 h-4 text-zinc-500" />
                )}
              </div>
              <p className="text-sm text-zinc-500">{community.category}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm text-zinc-400">Joined</span>
          </div>
        </div>

        <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
          {community.description || 'No description available'}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-zinc-400">
              <Users className="w-4 h-4" />
              <span className="text-sm">
                {formatNumber(community.memberCount || community.members || 0)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-zinc-500">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">
                {formatNumber(
                  community.discussionCount || community.posts || 0
                )}
              </span>
            </div>
          </div>
          <Button
            size="sm"
            className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
          >
            Enter Forum
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Smaller card for suggested communities
const SuggestedCommunityCard = ({ community, index, onJoin, isJoining }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.05 }}
    className="group flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
  >
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center text-sm font-bold">
        {community.name?.charAt(0) || 'C'}
      </div>
      <div>
        <h4 className="font-medium text-white text-sm group-hover:text-violet-300 transition-colors">
          {community.name}
        </h4>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>{community.category}</span>
          <span>•</span>
          <span>{formatNumber(community.memberCount || 0)} members</span>
        </div>
      </div>
    </div>
    <Button
      size="sm"
      variant="outline"
      className="rounded-full border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:border-violet-500/50"
      onClick={() => onJoin(community.oid || community._id)}
      disabled={isJoining}
    >
      {isJoining ? 'Joining...' : 'Join'}
    </Button>
  </motion.div>
);

// Loading skeleton
const CommunityCardSkeleton = () => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-6 animate-pulse">
    <div className="flex items-start gap-4 mb-4">
      <div className="h-14 w-14 rounded-xl bg-zinc-800" />
      <div className="space-y-2">
        <div className="h-5 w-32 bg-zinc-800 rounded" />
        <div className="h-4 w-20 bg-zinc-800 rounded" />
      </div>
    </div>
    <div className="h-10 w-full bg-zinc-800 rounded mb-4" />
    <div className="flex justify-between">
      <div className="flex gap-4">
        <div className="h-4 w-16 bg-zinc-800 rounded" />
        <div className="h-4 w-16 bg-zinc-800 rounded" />
      </div>
      <div className="h-8 w-24 bg-zinc-800 rounded-full" />
    </div>
  </div>
);

export default function CommunitySelectorPage() {
  // Use auth store for Passport SSO authentication
  const isStoreAuthenticated = useAuthStore(selectIsAuthenticated);
  const storeUser = useAuthStore((state) => state.user);

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { useGetAllCommunities, useGetUserCommunities, useJoinCommunity } =
    useCommunities();

  // Get user's joined communities - use store user's oid
  const { data: userCommunities, isLoading: loadingUserCommunities } =
    useGetUserCommunities(storeUser?.oid || storeUser?.passportId);

  // Get all communities for suggestions
  const { data: allCommunities, isLoading: loadingAll } =
    useGetAllCommunities();

  // Join community mutation
  const joinMutation = useJoinCommunity();

  // Check for saved community and auto-redirect
  useEffect(() => {
    const savedCommunity = getSelectedCommunity();
    if (savedCommunity && storeUser && userCommunities && !loadingUserCommunities) {
      // Verify user is still a member of the saved community
      const isMember = userCommunities.some(
        (c) => c.slug === savedCommunity.slug || c.oid === savedCommunity.oid
      );

      if (isMember) {
        // Auto-redirect to saved community
        navigate(`/forum/${savedCommunity.slug}`, { replace: true });
      }
    }
  }, [storeUser, userCommunities, loadingUserCommunities, navigate]);

  // Filter communities user hasn't joined for suggestions
  const suggestedCommunities = useMemo(() => {
    if (!allCommunities) return [];
    const joinedIds = new Set(
      (userCommunities || []).map((c) => c.oid || c._id)
    );
    return allCommunities
      .filter((c) => !joinedIds.has(c.oid || c._id))
      .slice(0, 5);
  }, [allCommunities, userCommunities]);

  // Filter joined communities by search
  const filteredUserCommunities = useMemo(() => {
    if (!userCommunities) return [];
    if (!searchTerm) return userCommunities;
    const search = searchTerm.toLowerCase();
    return userCommunities.filter(
      (c) =>
        c.name?.toLowerCase().includes(search) ||
        c.description?.toLowerCase().includes(search)
    );
  }, [userCommunities, searchTerm]);

  const handleJoinCommunity = async (communityId) => {
    if (!isStoreAuthenticated || !storeUser) {
      navigate('/sign-in');
      return;
    }

    // Check if user has completed registration and is verified
    if (!storeUser.isVerified) {
      toast.error('Complete Your Profile', {
        description: 'Please complete your profile verification to join communities.',
        action: {
          label: 'Complete Profile',
          onClick: () => navigate('/profile/complete-registration'),
        },
        duration: 5000,
      });
      return;
    }

    // Check max community limit (2 communities per user)
    if (userCommunities && userCommunities.length >= 2) {
      toast.error('Community Limit Reached', {
        description:
          'You can only join up to 2 communities. Please leave a community to join a new one.',
        duration: 5000,
      });
      return;
    }

    try {
      await joinMutation.mutateAsync({
        communityOid: communityId,
        creator: storeUser.wallet || storeUser.creator || storeUser.passportId,
      });
      toast.success('Successfully joined the community!');
    } catch (error) {
      console.error('Failed to join community:', error);
      toast.error(error?.response?.data?.error || 'Failed to join community');
    }
  };

  const handleCreateCommunity = () => {
    if (!isStoreAuthenticated || !storeUser) {
      navigate('/sign-in');
      return;
    }

    // Check if user has completed registration and is verified
    if (!storeUser.isVerified) {
      toast.error('Complete Your Profile', {
        description: 'Please complete your profile verification to create a community.',
        action: {
          label: 'Complete Profile',
          onClick: () => navigate('/profile/complete-registration'),
        },
        duration: 5000,
      });
      return;
    }

    navigate('/forum/create');
  };

  // User is authenticated if they have a valid Passport session with a verified profile
  const isAuthenticated = isStoreAuthenticated && storeUser;
  const hasJoinedCommunities = userCommunities && userCommunities.length > 0;

  return (
    <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
      {/* Background gradient */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[30%] w-[50%] h-[50%] rounded-full bg-violet-900/20 blur-[150px]" />
        <div className="absolute bottom-[-10%] right-[20%] w-[40%] h-[40%] rounded-full bg-cyan-900/20 blur-[150px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10 pt-24 pb-16">
        <div className="container px-4 md:px-6 max-w-5xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Choose Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                Community
              </span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-xl mx-auto">
              Select a community to participate in discussions, share knowledge,
              and connect with like-minded individuals.
            </p>
          </motion.div>

          {/* Not signed in state */}
          {!isAuthenticated && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
                <Users className="w-10 h-10 text-violet-400" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Sign in to Continue</h2>
              <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                Connect your wallet to join communities and participate in forum
                discussions.
              </p>
              <Link to="/sign-in">
                <Button
                  size="lg"
                  className="rounded-full bg-violet-600 hover:bg-violet-700 text-white min-h-12 px-8"
                >
                  Sign In to Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          )}

          {/* Authenticated - Show communities */}
          {isAuthenticated && (
            <>
              {/* Your Communities Section */}
              <section className="mb-16">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                    <h2 className="text-xl font-bold">Your Communities</h2>
                    {hasJoinedCommunities && (
                      <span className="text-sm text-zinc-500">
                        ({userCommunities.length})
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    {hasJoinedCommunities && (
                      <div className="relative flex-1 sm:flex-initial">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                          type="text"
                          placeholder="Search your communities..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full sm:w-[250px] h-10 pl-9 pr-4 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all"
                        />
                      </div>
                    )}

                    <Button
                      className="rounded-full bg-violet-600 hover:bg-violet-700 text-white whitespace-nowrap"
                      onClick={handleCreateCommunity}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create
                    </Button>
                  </div>
                </div>

                {loadingUserCommunities ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CommunityCardSkeleton />
                    <CommunityCardSkeleton />
                  </div>
                ) : hasJoinedCommunities ? (
                  <>
                    {filteredUserCommunities.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredUserCommunities.map((community, idx) => (
                          <JoinedCommunityCard
                            key={community.oid || community._id || idx}
                            community={community}
                            index={idx}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-white/5 rounded-2xl border border-white/10">
                        <p className="text-zinc-400">
                          No communities match "{searchTerm}"
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12 bg-gradient-to-br from-violet-500/5 to-cyan-500/5 rounded-2xl border border-violet-500/20"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/10 mb-4">
                      <Users className="w-8 h-8 text-violet-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      No Communities Yet
                    </h3>
                    <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">
                      Join a community below to start participating in
                      discussions, or create your own community.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/discover">
                        <Button
                          variant="outline"
                          className="rounded-full border-white/20 hover:bg-white/10"
                        >
                          Browse Communities
                        </Button>
                      </Link>
                      <Button
                        className="rounded-full bg-violet-600 hover:bg-violet-700"
                        onClick={handleCreateCommunity}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Community
                      </Button>
                    </div>
                  </motion.div>
                )}
              </section>

              {/* Suggested Communities Section */}
              {suggestedCommunities.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-6">
                    <TrendingUp className="w-5 h-5 text-cyan-400" />
                    <h2 className="text-xl font-bold">Suggested Communities</h2>
                  </div>

                  <div className="space-y-3">
                    {loadingAll
                      ? Array.from({ length: 3 }).map((_, idx) => (
                          <div
                            key={idx}
                            className="h-16 rounded-xl bg-white/5 animate-pulse"
                          />
                        ))
                      : suggestedCommunities.map((community, idx) => (
                          <SuggestedCommunityCard
                            key={community.oid || community._id || idx}
                            community={community}
                            index={idx}
                            onJoin={handleJoinCommunity}
                            isJoining={joinMutation.isPending}
                          />
                        ))}
                  </div>

                  <div className="mt-6 text-center">
                    <Link to="/discover">
                      <Button
                        variant="ghost"
                        className="rounded-full text-zinc-400 hover:text-white hover:bg-white/10"
                      >
                        View All Communities
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
